import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { useBackgroundAudio } from '@/hooks/useBackgroundAudio';

// Provide a single, global background audio controller for the entire app
export interface GlobalBackgroundAudioContextValue {
  backgroundAudioUrl: string | null;
  isBackgroundReady: boolean;
  isBackgroundPlaying: boolean;
  backgroundVolume: number;
  ensureBackgroundAudioLoaded: (categoryId?: string, musicFileName?: string) => Promise<void>;
  updateBackgroundVolume: (volume: number) => void;
  playBackgroundAudio: () => Promise<void>;
  pauseBackgroundAudio: () => void;
  startAutoStopTimer: (delaySeconds?: number) => void;
  cancelAutoStopTimer: () => void;
}

const BackgroundAudioContext = createContext<GlobalBackgroundAudioContextValue | undefined>(undefined);

export const BackgroundAudioProvider: React.FC<{ children: React.ReactNode }>= ({ children }) => {
  const bg = useBackgroundAudio();

  // No auto-loading - let user-selected background music handle it
  // useEffect(() => {
  //   console.log('🎵 BackgroundAudioProvider initializing with default music...');
  //   bg.ensureBackgroundAudioLoaded('global', 'universal_background.mp3');
  // }, []);

  const value = useMemo<GlobalBackgroundAudioContextValue>(() => ({
    backgroundAudioUrl: bg.backgroundAudioUrl,
    isBackgroundReady: bg.isBackgroundReady,
    isBackgroundPlaying: bg.isBackgroundPlaying,
    backgroundVolume: bg.backgroundVolume,
    ensureBackgroundAudioLoaded: bg.ensureBackgroundAudioLoaded,
    updateBackgroundVolume: bg.updateBackgroundVolume,
    playBackgroundAudio: bg.playBackgroundAudio,
    pauseBackgroundAudio: bg.pauseBackgroundAudio,
    startAutoStopTimer: bg.startAutoStopTimer,
    cancelAutoStopTimer: bg.cancelAutoStopTimer,
  }), [bg.backgroundAudioUrl, bg.isBackgroundReady, bg.isBackgroundPlaying, bg.backgroundVolume, bg.ensureBackgroundAudioLoaded, bg.updateBackgroundVolume, bg.playBackgroundAudio, bg.pauseBackgroundAudio, bg.startAutoStopTimer, bg.cancelAutoStopTimer]);

  return (
    <BackgroundAudioContext.Provider value={value}>
      {children}
    </BackgroundAudioContext.Provider>
  );
};

export const useGlobalBackgroundAudio = () => {
  const ctx = useContext(BackgroundAudioContext);
  if (!ctx) throw new Error('useGlobalBackgroundAudio must be used within BackgroundAudioProvider');
  return ctx;
};
